/**
 * @file packages/common/src/review/tiers.js
 * @description The three kinds of review, held once: the code the data
 * stores, the SLA route it runs on, and the Lexicon word a person reads.
 *
 * Walked 2026-09-11 as a senior government consultant (GC-058). The queue's
 * filter chips said Priority Review / Expert Review / Peer Review, the jobs
 * said `community` / `subscription` / `urgent`, the SLA said `express`, and
 * the request picker, the home page, the queue row and the server's own job
 * titles each kept a private copy of the mapping between them. Three
 * vocabularies and five copies of the join, so a chip could name a route the
 * pool did not filter on and nothing would say so.
 *
 * So: ONE table. Every surface reads the word from here, and every stored or
 * routed spelling resolves to the same row through `reviewTier()`.
 *
 * The words are the Lexicon's (foundation/dictionary/public/i18n/en/lexicon.json
 * → WORDS.PEER_REVIEW / EXPERT_REVIEW / PRIORITY_REVIEW); `lexiconKey` names
 * the entry, and tests/unit/reviewTiers.test.js fails if the two ever drift.
 * Same arrangement as progression/rungs.js: code is the source the services
 * import, the Lexicon is what prompts and copy are written from.
 *
 * `defaultMox` is the tier's floor price before any environment override —
 * the value requirements' Pricing.js used to declare privately. It lives here
 * so the rate a reviewer is shown and the rate the customer is charged read
 * one row; Pricing.js still applies the `MOX_PRICE_TIER_*` overrides.
 */

export const REVIEW_TIERS = Object.freeze([
  Object.freeze({
    code: 'community',
    route: 'community',
    lexiconKey: 'PEER_REVIEW',
    word: 'Peer Review',
    short: 'Peer',
    plain: 'a free check by another member',
    paid: false,
    defaultMox: 0,
    // Spellings records and older clients carry. `free` is the request
    // picker's id; `peer` is the Lexicon's own short form.
    aliases: Object.freeze(['free', 'peer', 'peer_review', 'peer-review'])
  }),
  Object.freeze({
    code: 'subscription',
    route: 'subscription',
    lexiconKey: 'EXPERT_REVIEW',
    word: 'Expert Review',
    short: 'Expert',
    plain: 'a check by an accredited reviewer',
    paid: true,
    defaultMox: 490,
    aliases: Object.freeze(['expert', 'expert_review', 'expert-review', 'standard'])
  }),
  Object.freeze({
    code: 'urgent',
    route: 'express',
    lexiconKey: 'PRIORITY_REVIEW',
    word: 'Priority Review',
    short: 'Priority',
    plain: 'an Expert Review within 36 hours',
    paid: true,
    defaultMox: 1990,
    aliases: Object.freeze(['express', 'priority', 'priority_review', 'priority-review', 'rush'])
  })
]);

export const REVIEW_TIER_CODES = Object.freeze(REVIEW_TIERS.map((t) => t.code));

const BY_ANY = Object.freeze(Object.fromEntries(
  REVIEW_TIERS.flatMap((t) => [t.code, t.route, ...t.aliases].map((k) => [k, t]))
));

/** The tier row for a stored code, an SLA route or any alias — or null. */
export const reviewTier = (value) => BY_ANY[String(value || '').trim().toLowerCase()] || null;

/**
 * The word a person reads. Never a raw code: an unknown value falls back to
 * the Lexicon's generic word, because "Review" is a worse answer than the
 * right name and a far better one than `urgent` on a reviewer's screen.
 */
export const reviewTierWord = (value) => reviewTier(value)?.word || 'Review';

/** The stored tier code for anything (route, alias, code), or null. */
export const reviewTierCode = (value) => reviewTier(value)?.code || null;

/** The SLA route a tier runs on, or null. */
export const reviewTierRoute = (value) => reviewTier(value)?.route || null;

export default { REVIEW_TIERS, REVIEW_TIER_CODES, reviewTier, reviewTierWord, reviewTierCode, reviewTierRoute };
